import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

const columnRef = z
  .string()
  .min(1)
  .describe(
    "Column name (e.g. 'Product Number') or 1-based column number (e.g. '3').",
  );

export const joinDocumentsStep = defineStepConfig({
  type: "join_documents",
  name: "Join Documents",
  description:
    "Joins two CSV documents on one or more configurable column pairs. " +
    "Supports left join (keep all left rows) and inner join (matched rows only). " +
    "Column references accept a header name or a 1-based column number.",
  category: "Data Access",

  inputSchema: z.object({
    leftDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "Primary document — every row appears in the output (left join) or only matched rows (inner join).",
    ),
    rightDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "Lookup document — rows matched against the left document.",
    ),
  }),

  outputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "Merged CSV with columns from both documents.",
    ),
    matchedCount: z
      .number()
      .describe("Left rows that found a match in the right document."),
    unmatchedCount: z
      .number()
      .describe("Left rows with no match in the right document."),
  }),

  configSchema: z.object({
    matchRules: z
      .array(
        z.object({
          leftColumn: columnRef.describe("Column in leftDocument to match on."),
          rightColumn: columnRef.describe(
            "Column in rightDocument to match on.",
          ),
          comparison: z
            .enum(["exact", "numeric", "contains"])
            .default("exact")
            .describe(
              "exact: case-insensitive trimmed equality. " +
                "numeric: parse as float before comparing. " +
                "contains: left cell contains right cell value (triggers linear scan, use sparingly).",
            ),
        }),
      )
      .min(1)
      .describe(
        "Match rules. Behavior depends on matchMode: " +
          "all = AND (every rule must match); any = OR (first matching rule wins, tried in order).",
      ),

    matchMode: z
      .enum(["all", "any"])
      .default("all")
      .describe(
        "all: every rule must match simultaneously (AND). " +
          "any: rules are tried in order; the first one that produces a match is used (priority OR). " +
          "Use 'any' for cascading fallback lookups (e.g. exact → prefix → cleaned string).",
      ),

    matchedRuleColumn: z
      .string()
      .optional()
      .describe(
        "When matchMode is 'any', optionally adds a column recording which rule matched " +
          "(1-based rule number as a string, empty if no match). Ignored when matchMode is 'all'.",
      ),

    joinType: z
      .enum(["left", "inner"])
      .default("left")
      .describe(
        "left: keep all left rows; right cells empty when unmatched. " +
          "inner: only output rows that matched.",
      ),

    rightPrefix: z
      .string()
      .optional()
      .describe(
        "Prefix prepended to every right-side header in the output (e.g. 'load_'). " +
          "Prevents column name collisions when both documents share header names.",
      ),

    matchedFlagColumn: z
      .string()
      .optional()
      .describe(
        "When set, adds a boolean column with this name (true/false) " +
          "indicating whether the left row found a match.",
      ),

    outputColumns: z
      .array(
        z.object({
          source: z
            .enum(["left", "right"])
            .describe("Which document this column comes from."),
          column: columnRef.describe(
            "Column name or 1-based number in the source document.",
          ),
          outputName: z
            .string()
            .optional()
            .describe(
              "Rename this column in the output. Defaults to the original header (with rightPrefix applied for right-side columns).",
            ),
        }),
      )
      .optional()
      .describe(
        "Explicit output column list. When omitted, all left columns are output first, " +
          "then all right columns (with rightPrefix applied).",
      ),
  }),

  emits: [] as const,
});
